import { localeFormatter } from './format'

/**
 * Calendar names and dates in the reader's locale.
 *
 * Month and weekday names are **universal CLDR data, not editorial vocabulary**
 * (→ invariants.md#facts-canonical-store-editorial): the warehouse stores a neutral index
 * and the platform supplies the name, so no translator maintains a list of twelve months
 * per locale and no locale can silently fall back to English.
 *
 * Every helper is **total** — it takes whatever the copy pipeline threads through it and
 * returns `null` rather than throwing, because these fill template tokens and an
 * unformattable value should drop its sentence, not the page.
 *
 * All of them read through `localeFormatter`, which is pinned to UTC, and build their
 * reference dates at UTC — so the rendered name always matches the index or date string
 * it came from, in every process zone (→ invariants.md#ssr-safe-rendered-dates).
 */

/** A 1-12 month index as the locale's month name; `null` if it is not one. */
export function monthName(
    index: unknown,
    locale: string,
    width: 'long' | 'short' = 'long',
): string | null {
    const n = toIndex(index)
    if (n === null || n < 1 || n > 12) {
        return null
    }
    return localeFormatter(locale).dateTime(new Date(Date.UTC(2000, n - 1, 1)), { month: width })
}

/**
 * A 1-7 (Monday-Sunday) weekday index as the locale's weekday name; `null` if it is not one.
 *
 * 2024-01-01 is a Monday, so the index doubles as that week's day-of-month — which is what
 * lets a neutral index become a name without a lookup table to maintain.
 */
export function weekdayName(
    index: unknown,
    locale: string,
    width: 'long' | 'short' = 'long',
): string | null {
    const n = toIndex(index)
    if (n === null || n < 1 || n > 7) {
        return null
    }
    return localeFormatter(locale).dateTime(new Date(Date.UTC(2024, 0, n)), { weekday: width })
}

/**
 * The `Date` a `YYYY-MM-DD` string names, at UTC; `null` if the value is not one.
 *
 * The single parse for every date string this app renders — `longDate` here,
 * `formatShortDate`, the C2C date strip, the chart axes — so malformed input has one
 * behavior instead of one per call site. Building from explicit parts at UTC is what
 * makes the rendered day zone-independent (→ invariants.md#ssr-safe-rendered-dates),
 * but `Date.UTC` also *normalizes overflow*: `2026-13-45` becomes 14 February 2027
 * rather than an error. So the parts are range-checked by round-tripping them through
 * the built date — a rendered date must be wrong loudly or not at all, never
 * confidently wrong. A trailing time is ignored rather than rejected, because the copy
 * pipeline threads full timestamps through `longDate`.
 */
export function dateFromIso(iso: unknown): Date | null {
    if (typeof iso !== 'string') {
        return null
    }
    const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
    if (!parts) {
        return null
    }
    const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])]
    const date = new Date(Date.UTC(year, month - 1, day))
    if (
        date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day
    ) {
        return null
    }
    return date
}

/** A `YYYY-MM-DD` string as the locale's long date ("February 12, 2026"); `null` if it is
 *  not one. Built from the parsed parts at UTC, so the rendered day is the day the string
 *  names rather than whatever instant a `Date` constructor would infer from it. */
export function longDate(iso: unknown, locale: string): string | null {
    const date = dateFromIso(iso)
    if (!date) {
        return null
    }
    return localeFormatter(locale).dateTime(date, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    })
}

/** Coerce a template variable to an integer index. Strings are accepted because the copy
 *  pipeline's variable bag is untyped; anything else, or a non-integer, is not an index. */
function toIndex(value: unknown): number | null {
    const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
    return Number.isInteger(n) ? n : null
}
